import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/app";
import { toast } from "sonner";
import {
  Plus, Trash, Lightning, CheckCircle, XCircle, Key,
  Buildings, ShieldCheck, ArrowClockwise
} from "@phosphor-icons/react";

const fetcher = (url) => api.get(url).then((r) => r.data);

/** Turn any API error into a display-safe string (handles 422 arrays). */
function errToString(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (detail) { try { return JSON.stringify(detail); } catch { return String(detail); } }
  return e?.message || "Chyba";
}

export default function AdminTidoCredentials() {
  const { data: list = [], mutate, isLoading } = useSWR("/tido/credentials", fetcher);
  const { data: cfg } = useSWR("/tido/config", fetcher);
  const [form, setForm] = useState({ subdomena: "", domain: "", password: "", label: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(null); // domain currently being processed
  const [testResult, setTestResult] = useState({}); // {[domain]: {ok, msg}}

  const suffix = cfg?.domain_suffix || "otido.sk";
  const effectiveDomain = form.domain?.trim() || (form.subdomena ? `${form.subdomena.trim().toLowerCase()}.${suffix}` : "");

  const submit = async () => {
    const domain = effectiveDomain;
    if (!domain || !form.password) {
      toast.error("Vyplňte subdoménu (alebo celú doménu) a heslo.");
      return;
    }
    setBusy(domain);
    try {
      await api.post("/tido/credentials", {
        domain,
        password: form.password,
        label: form.label || null,
      });
      toast.success(`Uložené: ${domain}`);
      setForm({ subdomena: "", domain: "", password: "", label: "" });
      setShowAdd(false);
      mutate();
    } catch (e) {
      toast.error(errToString(e));
    } finally {
      setBusy(null);
    }
  };

  const testAuth = async (domain) => {
    setBusy(domain);
    setTestResult((r) => ({ ...r, [domain]: null }));
    try {
      const { data } = await api.post(`/tido/credentials/${encodeURIComponent(domain)}/authenticate`);
      setTestResult((r) => ({
        ...r,
        [domain]: { ok: true, msg: `Hash získaný (${data.hash.slice(0, 20)}…)` },
      }));
      toast.success(`${domain}: login OK`);
      mutate();
    } catch (e) {
      setTestResult((r) => ({ ...r, [domain]: { ok: false, msg: errToString(e) } }));
      toast.error(`${domain}: ${errToString(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const del = async (domain) => {
    if (!window.confirm(`Naozaj vymazať kredencie pre ${domain}?`)) return;
    setBusy(domain);
    try {
      await api.delete(`/tido/credentials/${encodeURIComponent(domain)}`);
      toast.success(`Vymazané: ${domain}`);
      mutate();
    } catch (e) {
      toast.error(errToString(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-8" data-testid="admin-tido-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="label-eyebrow mb-1">Admin</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">TiDo firmy & heslá</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
            Každá firma sa loguje na svojej vlastnej subdoméne (napr. <code>cooppp.{suffix}</code>).
            Heslo (v MD5 formáte, ako to očakáva TiDo API) uložíš tu — systém ho automaticky
            použije pri kliknutí &quot;Analýza&quot; z TiDo appky s odpovedajúcim{" "}
            <code>?subdomena=</code> parametrom.
          </p>
          {cfg && (
            <div className="mt-3 text-xs text-[var(--text-muted)] tabular">
              Aktívne prostredie: <strong>{cfg.env_label || "—"}</strong> ·
              API base: <code>{cfg.base_url}</code> ·
              doména: <code>*.{cfg.domain_suffix}</code>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="add-credential-btn"
        >
          <Plus size={16} weight="bold" /> {showAdd ? "Zrušiť" : "Pridať firmu"}
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="card-flat p-6 mb-6" data-testid="add-credential-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="label-eyebrow block mb-1">Subdoména</label>
              <input
                type="text"
                placeholder="cooppp"
                value={form.subdomena}
                onChange={(e) => setForm({ ...form, subdomena: e.target.value, domain: "" })}
                className="w-full"
                data-testid="cred-subdomena-input"
              />
              <div className="text-[10px] text-[var(--text-muted)] mt-1">
                → {form.subdomena ? `${form.subdomena}.${suffix}` : `firma.${suffix}`}
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">alebo celá doména</label>
              <input
                type="text"
                placeholder={`iny-format.${suffix}`}
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value, subdomena: "" })}
                className="w-full"
                data-testid="cred-domain-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Heslo (MD5)</label>
              <input
                type="password"
                placeholder="d2b64f2ce…"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full"
                data-testid="cred-password-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Popis (voliteľné)</label>
              <input
                type="text"
                placeholder="Coop SD Poprad"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full"
                data-testid="cred-label-input"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => { setShowAdd(false); setForm({ subdomena: "", domain: "", password: "", label: "" }); }}
              className="btn-ghost"
            >
              Zrušiť
            </button>
            <button
              onClick={submit}
              disabled={busy === effectiveDomain}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="cred-save-btn"
            >
              <ShieldCheck size={16} /> Uložiť
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading && <div className="text-sm text-[var(--text-secondary)]">Načítavam…</div>}
      {!isLoading && list.length === 0 && (
        <div className="card-flat p-8 text-center text-sm text-[var(--text-secondary)]">
          <Buildings size={40} weight="duotone" className="mx-auto mb-3 text-[var(--text-muted)]" />
          Ešte žiadne kredencie. Pridajte prvú firmu tlačidlom hore.
        </div>
      )}
      {list.length > 0 && (
        <div className="card-flat overflow-hidden" data-testid="credentials-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] bg-[var(--bg-surface-hover)]">
                <th className="text-left p-3 label-eyebrow">Doména</th>
                <th className="text-left p-3 label-eyebrow">Popis</th>
                <th className="text-left p-3 label-eyebrow">Heslo</th>
                <th className="text-left p-3 label-eyebrow">Hash</th>
                <th className="text-left p-3 label-eyebrow">Test výsledok</th>
                <th className="text-right p-3 label-eyebrow">Akcie</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const tr = testResult[c.domain];
                return (
                  <tr key={c.domain} className="border-b border-[var(--border-default)] last:border-0" data-testid={`cred-row-${c.domain}`}>
                    <td className="p-3 font-mono text-xs">
                      <div className="inline-flex items-center gap-2">
                        <Buildings size={14} className="text-[var(--text-muted)]" />
                        {c.domain}
                      </div>
                    </td>
                    <td className="p-3 text-[var(--text-secondary)]">{c.label || "—"}</td>
                    <td className="p-3">
                      {c.has_password ? (
                        <span className="inline-flex items-center gap-1 text-[var(--success)] text-xs"><Key size={12} weight="fill" /> uložené</span>
                      ) : (
                        <span className="text-[var(--critical)] text-xs">chýba</span>
                      )}
                    </td>
                    <td className="p-3">
                      {c.has_hash ? (
                        <span className="inline-flex items-center gap-1 text-[var(--success)] text-xs"><CheckCircle size={12} weight="fill" /> platný</span>
                      ) : (
                        <span className="text-[var(--text-muted)] text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {tr && (tr.ok ? (
                        <span className="text-[var(--success)] inline-flex items-center gap-1" data-testid={`cred-test-ok-${c.domain}`}><CheckCircle size={12} weight="fill" /> {tr.msg}</span>
                      ) : (
                        <span className="text-[var(--critical)] inline-flex items-center gap-1" data-testid={`cred-test-err-${c.domain}`} title={tr.msg}>
                          <XCircle size={12} weight="fill" /> {tr.msg.length > 40 ? tr.msg.slice(0, 40) + "…" : tr.msg}
                        </span>
                      ))}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => testAuth(c.domain)}
                          disabled={busy === c.domain}
                          className="btn-ghost inline-flex items-center gap-1 text-xs px-2 py-1 disabled:opacity-50"
                          data-testid={`cred-test-btn-${c.domain}`}
                          title="Otestovať login s uloženými údajmi"
                        >
                          {busy === c.domain ? <ArrowClockwise size={12} className="animate-spin" /> : <Lightning size={12} />}
                          Test
                        </button>
                        <button
                          onClick={() => del(c.domain)}
                          disabled={busy === c.domain}
                          className="btn-ghost inline-flex items-center gap-1 text-xs px-2 py-1 text-[var(--critical)] disabled:opacity-50"
                          data-testid={`cred-delete-btn-${c.domain}`}
                          title="Vymazať"
                        >
                          <Trash size={12} /> Zmazať
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 card-flat p-4 text-xs text-[var(--text-secondary)] leading-relaxed">
        <div className="label-eyebrow mb-2">Ako to funguje</div>
        <ol className="list-decimal ml-4 space-y-1">
          <li>V TiDo appke (PHP) použite link typu <code>https://analyza.tido.sk/tido-uvitanie?IDP=8&ROK=2026&MESIAC=6&subdomena=cooppp</code></li>
          <li>Naša appka z <code>subdomena=cooppp</code> poskladá plnú doménu (<code>cooppp.{suffix}</code>), vyhľadá tu uložené heslo</li>
          <li>Zavolá <code>{cfg?.base_url || "TIDO_BASE_URL"}/api/api-client/login</code> s tým heslom → dostane hash</li>
          <li>Ťahá realita/plán/shift-times mesiac po mesiaci a ukladá do MongoDB</li>
        </ol>
      </div>
    </div>
  );
}
